// Quiet refresh: reload something other people may have changed (the steps of an episode) when the window comes
// back (focus, tab visible again) and every `everyMs` while the tab is visible. It never overlaps itself and runs
// no more than once per `minGapMs`, so a focus and a visibilitychange together make one request. The caller keeps
// its state while a run is in flight (no spinner) and decides what to do with the answer. Pure of React: the
// window, document and timers are passed in, so the logic is tested without a browser.

export interface QuietRefreshHost {
  /** "visible" while the tab is in front */
  visibilityState: () => string;
  /** subscribe to the window's focus and the document's visibilitychange; answers how to unsubscribe */
  onWake: (wake: () => void) => () => void;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (id: unknown) => void;
  now: () => number;
}

export interface QuietRefreshOptions {
  /** reload; a failure is the caller's to swallow (a quiet refresh keeps what is on screen) */
  run: () => Promise<unknown>;
  /** default 20 s */
  everyMs?: number;
  /** default 1 s: two wake-ups this close make one run */
  minGapMs?: number;
  host: QuietRefreshHost;
}

export const QUIET_REFRESH_MS = 20_000;

/** Starts the refresh; the returned function stops it. */
export function startQuietRefresh(o: QuietRefreshOptions): () => void {
  const { host } = o;
  const gap = o.minGapMs ?? 1000;
  let busy = false;
  let last = Number.NEGATIVE_INFINITY;
  let stopped = false;
  const go = () => {
    if (stopped || busy || host.visibilityState() !== "visible") return;
    if (host.now() - last < gap) return;
    busy = true;
    last = host.now();
    void Promise.resolve()
      .then(o.run)
      .catch(() => undefined)
      .finally(() => {
        busy = false;
      });
  };
  const off = host.onWake(go);
  const timer = host.setInterval(go, o.everyMs ?? QUIET_REFRESH_MS);
  return () => {
    stopped = true;
    off();
    host.clearInterval(timer);
  };
}

/** The browser's window, document and timers. */
export function browserHost(): QuietRefreshHost {
  return {
    visibilityState: () => document.visibilityState,
    onWake: (wake) => {
      const onVisible = () => {
        if (document.visibilityState === "visible") wake();
      };
      window.addEventListener("focus", wake);
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        window.removeEventListener("focus", wake);
        document.removeEventListener("visibilitychange", onVisible);
      };
    },
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id as number),
    now: () => Date.now(),
  };
}

/** The previous value when the new one says the same, so state that did not change causes no render. */
export function keepIfSame<T>(prev: T, next: T): T {
  if (prev === next) return prev;
  try {
    return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
  } catch {
    return next;
  }
}
